import axios from 'axios';
import { logDevApiError } from './utils/devApiDebug';
import { getDevApiBaseURL } from './utils/devApiBaseUrl';

const getAdminBaseURL = () => {
  if (process.env.NODE_ENV === 'production') {
    return 'https://api.lotwleague.com/api';
  }
  return getDevApiBaseURL();
};

const adminApi = axios.create({
  baseURL: getAdminBaseURL(),
  withCredentials: true
});

// Request interceptor
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error instanceof Error ? error : new Error(error))
);

adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    logDevApiError(error, 'admin');
    return Promise.reject(error);
  }
);

// Named exports for individual methods
export const getAllUsers = () => adminApi.get('/admin/users');
export const getUserDetails = (userId) => adminApi.get(`/admin/users/${userId}/details`);
export const updateUserRole = (userId, role) => adminApi.put(`/admin/users/${userId}/role`, { role });
export const resetUserPassword = (userId, password) => adminApi.put(`/admin/users/${userId}/password`, { password });
export const updateUserDetails = (userId, details) => adminApi.put(`/admin/users/${userId}`, details);
export const createOnboardingInvite = (payload) => adminApi.post('/admin/onboarding/invites', payload);
export const getOnboardingInvites = () => adminApi.get('/admin/onboarding/invites');
export const regenerateOnboardingInvite = (id) => adminApi.post(`/admin/onboarding/invites/${id}/regenerate`);
export const revokeOnboardingInvite = (id) => adminApi.post(`/admin/onboarding/invites/${id}/revoke`);

// Watch management
export const fetchWatchData = () => adminApi.get('/admin/watch-data');
export const deleteWatch = (userId, gameId, pickType, week) =>
  adminApi.delete(`/admin/watch-data/${userId}/${gameId}/${pickType}/${week}`);
export const purgeAllWatches = () => adminApi.delete('/admin/watch-data');

// Game management
export const fetchGamesForWeek = (week) => adminApi.get(`/admin/games?week=${week}`);
export const getAllGames = () => adminApi.get('/admin/games');
export const getGameDetails = (gameId) => adminApi.get(`/admin/games/${gameId}`);

// User activity
export const getUserWatches = (userId, year) => adminApi.get(`/admin/users/${userId}/watches?year=${year}`);
export const getUserLocks = (userId, year) => adminApi.get(`/admin/users/${userId}/locks?year=${year}`);

// Manual operations
export const createManualLock = (lockData) => adminApi.post('/admin/manual-lock', lockData);
export const triggerAudit = () => adminApi.post('/admin/trigger-audit');
export const triggerNoPicks = (week, season) => adminApi.post('/admin/trigger-no-picks', { week, season });
export const getAvailableWeeks = (season) => adminApi.get(`/admin/weeks${season ? `?season=${season}` : ''}`);
export const getAvailableSeasons = () => adminApi.get('/admin/seasons');

// API Status
export const getGlobalApiStatus = () => adminApi.get('/admin/global_api_status');
export const getApiTablesStatus = () => adminApi.get('/admin/api_tables_status');

// API Saver Mode
export const getApiSaverMode = () => adminApi.get('/admin/api_saver_mode');
export const setApiSaverMode = (enabled) => adminApi.post('/admin/api_saver_mode', { enabled });

export const getOffSeasonMode = () => adminApi.get('/admin/off_season_mode');
export const updateOffSeasonMode = (settings) => adminApi.post('/admin/off_season_mode', settings);

// Add version endpoint
export const getServerVersion = () => adminApi.get('/system/version');

// Logging endpoints
export const getPasswordResetLogs = (params = {}) => adminApi.get('/admin/logs/password-resets', { params });
export const getUserSigninLogs = (params = {}) => adminApi.get('/admin/logs/signins', { params });

// League Settings endpoints
export const getLeagueSettings = () => adminApi.get('/admin/league-settings');
export const updateLeagueSettings = (settings) => adminApi.put('/admin/league-settings', settings);
export const getEffectiveSettings = (weekNumber) => adminApi.get(`/admin/league-settings/effective/${weekNumber}`);

// Bye Week Requirement endpoints
export const getByeWeekRequirement = () => adminApi.get('/admin/league-settings/bye-week-required');
export const updateByeWeekRequirement = (required) => adminApi.put('/admin/league-settings/bye-week-required', { required });

// Database Management

// Discord settings (legacy)
export const getDiscordSettings = () => adminApi.get('/admin/discord/settings');
export const updateDiscordSettings = (settings) => adminApi.put('/admin/discord/settings', { settings });
export const getDiscordServerInvite = () => adminApi.get('/admin/discord/server-invite');
export const updateDiscordServerInvite = (url) => adminApi.put('/admin/discord/server-invite', { url });
export const testDiscordBroadcast = (channelId) => adminApi.post('/admin/discord/test', { channelId });
export const getDiscordLogs = (limit = 50) => adminApi.get(`/admin/discord/logs?limit=${limit}`);
export const getDiscordStatus = () => adminApi.get('/admin/discord/status');

// Discord Channel Management
export const getDiscordChannels = () => adminApi.get('/admin/discord/channels');
export const addDiscordChannel = (channelData) => adminApi.post('/admin/discord/channels', channelData);
export const updateDiscordChannel = (id, channelData) => adminApi.put(`/admin/discord/channels/${id}`, channelData);
export const deleteDiscordChannel = (id) => adminApi.delete(`/admin/discord/channels/${id}`);
export const updateChannelNameFromDiscord = (id) => adminApi.post(`/admin/discord/channels/${id}/update-name`);

// Discord Broadcast Mappings
export const getDiscordBroadcastMappings = () => adminApi.get('/admin/discord/broadcast-mappings');
export const setDiscordBroadcastMapping = (mappingData) => adminApi.post('/admin/discord/broadcast-mappings', mappingData);

// Drama Analysis
export const getDramaAnalysis = (weekNumber, season) => adminApi.get(`/admin/drama-analysis/${weekNumber}/${season}`);
export const getUnifiedDramaAnalysis = (weekNumber, season) => adminApi.get(`/admin/unified-drama-analysis/${weekNumber}/${season}`);

export const reanalyzeDrama = (weekNumber, season) => adminApi.post(`/admin/reanalyze-drama/${weekNumber}/${season}`);

// Playoff Management endpoints
export const getPlayoffStatus = (season) => adminApi.get(`/playoff/status/${season || ''}`);
export const getPlayoffAdmin = (playoffId) => adminApi.get(`/playoff/admin/${playoffId}`);
export const createPlayoff = (season, settings) => adminApi.post('/playoff/create', { season, settings });
export const approvePlayoff = (playoffId) => adminApi.post('/playoff/approve', { playoff_id: playoffId });
export const addPlayoffParticipant = (playoffId, userId, autoIdentified = false) => 
  adminApi.post('/playoff/participants/add', { playoff_id: playoffId, user_id: userId, auto_identified: autoIdentified });
export const removePlayoffParticipant = (playoffId, userId) => 
  adminApi.post('/playoff/participants/remove', { playoff_id: playoffId, user_id: userId });
export const revealPlayoffPredictions = (playoffId) => 
  adminApi.post('/playoff/reveal', { playoff_id: playoffId });
export const linkGameToPlayoff = (playoffId, gameId) => 
  adminApi.post('/playoff/link-game', { playoff_id: playoffId, game_id: gameId });
export const calculatePlayoffResults = (playoffId) => 
  adminApi.post('/playoff/calculate', { playoff_id: playoffId });
export const triggerPlayoffDeadline = (playoffId) => 
  adminApi.post(`/playoff/admin/${playoffId}/trigger-deadline`);
export const simulateGameCompletion = (playoffId, homeScore, awayScore) => 
  adminApi.post(`/playoff/admin/${playoffId}/simulate-game-completion`, { home_score: homeScore, away_score: awayScore });
export const listPostseasonGames = (season) => 
  adminApi.get(`/playoff/admin/list-postseason-games/${season || ''}`);
export const getPlayoffScoreGrid = (playoffId) => adminApi.get(`/playoff/score-grid/${playoffId}`);
export const checkAutoIdentifyTrigger = (season) => 
  adminApi.post(`/playoff/check-auto-identify/${season || ''}`);
export const findChampionshipGame = (season) => 
  adminApi.get(`/playoff/find-championship-game/${season || ''}`);
export const identifyTiedUsers = (season) => 
  adminApi.get(`/playoff/identify-tied-users/${season || ''}`);
export const analyzeFirstPlaceContenders = (season) => 
  adminApi.get(`/playoff/analyze-contenders/${season || ''}`);
export const deletePlayoff = (playoffId) => 
  adminApi.delete(`/playoff/admin/${playoffId}`);
export const unfinalizePlayoff = (playoffId) => 
  adminApi.post(`/playoff/admin/${playoffId}/unfinalize`);
export const updatePlayoffPrediction = (playoffId, predictionId, homeScore, awayScore, skipDuplicateCheck = false) => 
  adminApi.put(`/playoff/admin/${playoffId}/predictions/${predictionId}`, { home_score: homeScore, away_score: awayScore, skip_duplicate_check: skipDuplicateCheck });
export const deletePlayoffPrediction = (playoffId, predictionId) => 
  adminApi.delete(`/playoff/admin/${playoffId}/predictions/${predictionId}`);
export const createPlayoffPredictionForUser = (playoffId, userId, homeScore, awayScore, skipDuplicateCheck = false) => 
  adminApi.post(`/playoff/admin/${playoffId}/predictions/create`, { user_id: userId, home_score: homeScore, away_score: awayScore, skip_duplicate_check: skipDuplicateCheck });

// Weekly Recap Prompt API functions
export const getWeeklyRecapPrompts = () => adminApi.get('/admin/weekly-recap-prompts');
export const getWeeklyRecapPrompt = (weekNumber, season) => adminApi.get(`/admin/weekly-recap-prompts/${weekNumber}/${season}`);
export const updateWeeklyRecapPrompt = (weekNumber, season, promptPackage, status) =>
    adminApi.put(`/admin/weekly-recap-prompts/${weekNumber}/${season}`, { promptPackage, status });
export const updateWeeklyRecapResponse = (weekNumber, season, llmResponse) =>
    adminApi.put(`/admin/weekly-recap-prompts/${weekNumber}/${season}/recap`, { llmResponse });
export const generateWeeklyRecapPrompt = (weekNumber, season) =>
    adminApi.post(`/admin/weekly-recap-prompts/${weekNumber}/${season}/generate`);
export const sendPromptToLLM = (weekNumber, season, promptPackage) =>
    adminApi.post(`/admin/weekly-recap-prompts/${weekNumber}/${season}/send-to-llm`, { promptPackage });

// Admin Discord Notification API functions
export const notifyAdmins = (message) => adminApi.post('/admin/discord/notify-admins', { message });
export const getVerifiedAdmins = () => adminApi.get('/admin/discord/verified-admins');
export const testPromptNotification = (weekNumber, season) =>
    adminApi.post('/admin/discord/test-prompt-notification', { weekNumber, season });

// Dynamic Cron Service API functions
export const getDynamicCronStatus = () => adminApi.get('/admin/dynamic-cron/status');
export const scheduleWeeklyRecap = (weekNumber, season, lastLockTime) =>
    adminApi.post('/admin/dynamic-cron/schedule-recap', { weekNumber, season, lastLockTime });
export const cancelScheduledJob = (jobKey) => adminApi.delete(`/admin/dynamic-cron/cancel/${jobKey}`);
export const checkPendingRecaps = () => adminApi.post('/admin/dynamic-cron/check-pending');

// Enhanced Weekly Recap API functions
export const getNextGenerationTime = () => adminApi.get('/admin/weekly-recap-prompts/next-generation-time');
export const approvePrompt = (weekNumber, season) =>
    adminApi.post(`/admin/weekly-recap-prompts/${weekNumber}/${season}/approve-prompt`);
export const approveRecap = (weekNumber, season) =>
    adminApi.post(`/admin/weekly-recap-prompts/${weekNumber}/${season}/approve-recap`);
export const deletePromptPackage = (weekNumber, season) =>
    adminApi.delete(`/admin/weekly-recap-prompts/${weekNumber}/${season}`);
export const sendRecapNow = (weekNumber, season) =>
    adminApi.post(`/admin/weekly-recap-prompts/${weekNumber}/${season}/send-now`);
export const updateScheduling = (weekNumber, season, scheduledTime, cronJobKey) =>
    adminApi.put(`/admin/weekly-recap-prompts/${weekNumber}/${season}/scheduling`, { scheduledTime, cronJobKey });

// Pick Reminder API functions
export const sendFridayPickReminder = () => adminApi.post('/admin/pick-reminders/friday');
export const sendSaturdayPickReminder = () => adminApi.post('/admin/pick-reminders/saturday');

// Payment Collection API functions
export const getPaymentCollectionStatus = (season) => adminApi.get(`/payment-collection/status/${season || ''}`);
export const checkSeasonComplete = (season) => adminApi.get(`/payment-collection/season-complete/${season || ''}`);
export const checkPlayoffStatus = (season) => adminApi.get(`/payment-collection/playoff-status/${season || ''}`);
export const generatePaymentCollection = (season) => adminApi.post(`/payment-collection/generate/${season || ''}`);
export const previewPaymentNotifications = (season, useLLM = true) => adminApi.get(`/payment-collection/preview/${season || ''}?useLLM=${useLLM}`);
export const regenerateUserPaymentMessage = (season, userId, useLLM = true) => adminApi.post(`/payment-collection/regenerate/${season || ''}/user/${userId}`, { useLLM });
export const regenerateAllPaymentMessages = (season, useLLM = true) => adminApi.post(`/payment-collection/regenerate/${season || ''}`, { useLLM });
export const sendPaymentNotifications = (season, useLLM = true) => adminApi.post(`/payment-collection/send-notifications/${season || ''}`, { useLLM });
export const sendPaymentReminders = (season) => adminApi.post(`/payment-collection/send-reminders/${season || ''}`);
export const markPaymentAsPaid = (collectionId, paymentService) => adminApi.post(`/payment-collection/mark-paid/${collectionId}`, { paymentService });
export const getPaymentMethods = () => adminApi.get('/payment-collection/payment-methods');

// Season re-enrollment
export const getReenrollmentStatus = (season) =>
  adminApi.get(`/reenrollment/status/${season || ''}`);
export const createReenrollmentCampaign = (season, delays = {}) =>
  adminApi.post('/reenrollment/campaigns', { season, ...delays });
export const openReenrollmentCampaign = (campaignId) =>
  adminApi.post(`/reenrollment/campaigns/${campaignId}/open`);
export const reopenReenrollmentCampaign = (campaignId) =>
  adminApi.post(`/reenrollment/campaigns/${campaignId}/reopen`);
export const closeReenrollmentCampaign = (campaignId) =>
  adminApi.post(`/reenrollment/campaigns/${campaignId}/close`);
export const seedReenrollmentTargets = (campaignId) =>
  adminApi.post(`/reenrollment/campaigns/${campaignId}/seed`);
export const postReenrollmentPoll = (campaignId) =>
  adminApi.post(`/reenrollment/campaigns/${campaignId}/poll`);
export const inspectReenrollmentPoll = (campaignId, messageUrl) =>
  adminApi.post(`/reenrollment/campaigns/${campaignId}/inspect-poll`, { messageUrl });
export const linkReenrollmentPoll = (campaignId, messageUrl, answerMapping) =>
  adminApi.post(`/reenrollment/campaigns/${campaignId}/link-poll`, { messageUrl, answerMapping });
export const syncReenrollmentPoll = (campaignId) =>
  adminApi.post(`/reenrollment/campaigns/${campaignId}/sync-poll`);
export const runReenrollmentEscalation = (campaignId) =>
  adminApi.post(`/reenrollment/campaigns/${campaignId}/run-escalation`);
export const affirmReenrollmentTarget = (targetId, referralNote) =>
  adminApi.post(`/reenrollment/targets/${targetId}/affirm`, { referralNote });
export const declineReenrollmentTarget = (targetId, referralNote) =>
  adminApi.post(`/reenrollment/targets/${targetId}/decline`, { referralNote });
export const updateReenrollmentReferral = (targetId, referralNote) =>
  adminApi.patch(`/reenrollment/targets/${targetId}/referral`, { referralNote });
export const linkReenrollmentTarget = (targetId, userId) =>
  adminApi.post(`/reenrollment/targets/${targetId}/link`, { userId });
export const startReenrollmentCatFacts = (campaignId) =>
  adminApi.post(`/reenrollment/campaigns/${campaignId}/catfacts/start`);
export const stopReenrollmentCatFacts = () =>
  adminApi.post('/reenrollment/catfacts/stop');

// SMS Admin endpoints
export const getSMSStatistics = () => adminApi.get('/admin/sms/statistics');
export const sendTestSMS = (phoneNumber, message) => adminApi.post('/admin/sms/test', { phoneNumber, message });

// Also export the entire api object as default
export default adminApi;
